/*
  Popper.jsx

  Similar to React Popper, but doesn't need to auto-adjust or anything
  as our website's body never scrolls.
*/

// Npm.
import React, { useMemo, useEffect, useState, useRef } from 'react';

// Lib.
import isEventSupported from '../../utils/isEventSupported';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Helper to get position on the page as a percentage.
function getPosition(xPos, yPos) {

  const style = {};

  // Get position relative to document size.
  // TODO: Adjust position based on content size?
  const yP = (yPos / window.innerHeight) * 100;
  const xP = (xPos / window.innerWidth) * 100;
  if (xP > 80) {
    style.right = `${100 - xP}%`;
  } else {
    style.left = `${xP}%`;
  }
  if (yP > 80) {
    style.bottom = `${100 - yP}%`;
  } else {
    style.top = `${yP}%`;
  }

  // Make one of the borders sharper.
  const borderName = `border${style.top ? 'Top' : 'Bottom'}${style.left ? 'Left' : 'Right'}Radius`;
  style[borderName] = 0;
  return style;

}

// Helper to get the event name to use for clicking on the document.
function getMouseDownEventName() {
  return ['pointerdown', 'touchstart', 'mousedown'].find(name => isEventSupported(name));
}

// Handlers to stop propagation events.
function handleStopPropagation(event) {
  event.stopPropagation();
}
function handlePreventDefault(event) {
  event.preventDefault();
}

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    zIndex: 1200,
    backgroundColor: theme.color_paper,
    boxShadow: theme.shadow(1),
    borderRadius: '4px',
    overflow: 'hidden',
    maxWidth: 200,
    outline: 'none',
  },
}));

/**
 * Left sidebar component.
 */
export default function Popper({
  className,
  open = false,
  xPos = 0,
  yPos = 0,
  onClose,
  ...rest
}) {

  // Position as percentage to make sure it follows along with resizes.
  const [style, setStyle] = useState(getPosition(xPos, yPos));
  useEffect(() => {
    setStyle(getPosition(xPos, yPos));
  }, [xPos, yPos]);

  // Get className to use.
  const classes = useStyles();
  const cls = useMemo(() => cx('Popper', classes.root, className), [classes.root, className]);

  // Effect to close menu when mouse is down elsewhere.
  // https://stackoverflow.com/questions/41181372/chrome-mousedown-and-mouseup-events-no-longer-working-other-browsers-are-fine/41238807#41238807
  useEffect(() => {
    if (open && onClose) {
      const mouseDownEvent = getMouseDownEventName();
      if (mouseDownEvent) {

        // Main click event.
        function handleClick(event) {
          // In React 16 or less, event.stopPropagation is not enough on the div,
          // we need to check if the click is inside the div here too.
          if (rootRef.current && rootRef.current.contains(event.target)) {
            return;
          }
          onClose();
        }

        // Various button pressing events.
        // TODO: Maybe listen for any key down event?
        function handleKeyDown(event) {
          if (event.key === 'Enter') {
            onClose();
          }
        }
        function handleKeyUp(event) {
          if (event.key === ' ' || event.key === 'Spacebar') {
            onClose();
          }
        }

        // Add event listeners.
        if (mouseDownEvent) {
          document.addEventListener(mouseDownEvent, handleClick, false);
        }
        document.addEventListener('keydown', handleKeyDown, false);
        document.addEventListener('keyup', handleKeyUp, false);

        // Remove event listeners in cleanup.
        return () => {
          if (mouseDownEvent) {
            document.removeEventListener(mouseDownEvent, handleClick, false);
          }
          document.addEventListener('keydown', handleKeyDown, false);
          document.addEventListener('keyup', handleKeyUp, false);
        };

      }
    }
  }, [open, onClose]);

  // Effect to "focus" on the menu to make tab indexing correct.
  const rootRef = useRef();
  useEffect(() => {
    if (open && rootRef.current) {
      rootRef.current.focus();
    }
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={cls}
      style={style}
      onMouseDown={handleStopPropagation}
      onPointerDown={handleStopPropagation}
      onTouchStart={handleStopPropagation}
      onKeyDown={handleStopPropagation}
      onKeyUp={handleStopPropagation}
      onContextMenu={handlePreventDefault}
      tabIndex={-1}
      {...rest}
    />
  );
}
